import React, {
  useState,
  useImperativeHandle,
  forwardRef,
  useCallback,
  useMemo,
} from 'react'
import Spinner from '@components/common/spinner'
import { addComment, getComments } from '@data/api/comment'
import { FormattedMessage } from 'react-intl'
import useSwrInfinite from 'swr/infinite'
import useAuth from '@/hooks/auth'
import Comment from './comment'
import CommentInput from './input'
import messages from './messages'

const PER_PAGE = 20

const fetcher = async (
  [ , type, id, page ]: [
    type: string,
    id: string,
    url: string,
    page: number,
  ],
) => {
  const result = await getComments(type, id, PER_PAGE, page)
  if (!result || 'error' in result) {
    return []
  }
  return result
}
interface Props {
  type: string
  id: string
  owner: string
}

const CommentsContainer = ({ type, id, owner }: Props, ref) => {
  const [ isSubmitting, setSubmitting ] = useState(false)

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const getKey = useCallback((page: number, prevData: any[]) => {
    if (!!prevData && prevData.length === 0) {
      return null
    }
    return [ '/api/comments', type, id, page + 1 ]
  }, [ id, type ])

  const {
    data,
    setSize,
    isLoading,
    isValidating,
    mutate,
  } = useSwrInfinite(getKey, fetcher, {
    revalidateOnFocus: true,
    refreshInterval: 15000,
    revalidateAll: true,
  })
  const { loggedin, currentUser: currUser } = useAuth()
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const currentUser: any = loggedin ? currUser : { username: '' }

  const comments = useMemo(() => data ? data.flat() : [], [ data ])

  const onSubmit = async (text) => {
    setSubmitting(true)

    await addComment(type, id, text).catch((error) => ({
      error,
    }))

    setSubmitting(false)
    mutate()
  }

  useImperativeHandle(ref, () => ({
    onSubmit: (text) => onSubmit(text),
  }))

  if (isLoading) {
    return (
      <div className="tx-center">
        <Spinner size="1rem" />
        <span className="ml-3">Loading...</span>
      </div>
    )
  }

  return (
    <div>
      {type !== 'comment' && (
        <div className="mb-5">
          {currentUser.username ? (
            <CommentInput
              onSubmit={onSubmit}
              user={currentUser}
              submitting={isSubmitting}
            />
          ) : (
            <div className="tx-center tx-g900 tx-14 py-5">
              <FormattedMessage {...messages.notSignedIn} />
            </div>
          )}
        </div>
      )}
      {!!comments && comments.length > 0 ? (
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        comments.map((data: any) => (
          <Comment
            key={data.id}
            comment={data}
            owner={owner}
            user={currentUser}
            type={type}
            reload={mutate}
          />
        ))
      ) : (
        <div className="tx-center tx-g900 tx-14 py-5">
          <FormattedMessage {...messages.noComments} />
        </div>
      )}
      {comments.length >= PER_PAGE && (
        <>
          {!isValidating ? (
            <div className="tx-center">
              <button
                type="button"
                className="btn-chromeless tx-black tx-12 mr-3"
                aria-label="next"
                onClick={() => setSize((size) => size + 1)}
              >
                <span>さらに読み込む</span>
              </button>
            </div>
          ) : (
            <div className="tx-center">
              <Spinner size="1rem" />
              <span className="ml-3">Loading...</span>
            </div>
          )}
        </>
      )}
    </div>
  )
}

export { CommentsContainer }

export default forwardRef(CommentsContainer)
